import { compareValuesCaseSensitive } from '@services/utils/Helpers';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/AppState';
import { InitialFilterState } from '../../store/inventory/InitialFilterReducer';
import { setTrims } from '../../store/inventory/InventoryFilterActions';
import { InventoryFilterState } from '../../store/inventory/InventoryFilterReducer';
import { applyFilters, useFilteredInventoryData } from '../UseFilteredInventoryData';
import { useSearch } from '../UseSearch';
import { LabelType, UseDropdownLabel } from './UseDropdownLabel';

export const useTrims = () => {
  const filters = useSelector<AppState, InventoryFilterState>((state) => state.inventoryFilter);
  const initialFilters = useSelector<AppState, InitialFilterState>(
    (state) => state.initialInventoryFilter,
  );
  const { unfilteredData } = useFilteredInventoryData();
  const { searchText, setSearchText } = useSearch();
  const dispatch = useDispatch();

  const handleSearch = (text: string) => setSearchText(text);

  const handleChange = (trim: string) => {
    if (filters.trims.includes(trim)) {
      dispatch(setTrims(filters.trims.filter((_trim) => _trim !== trim)));
    } else {
      dispatch(setTrims([...filters.trims, trim]));
    }
  };

  const initiallySetAllTrimsAsPicked = () => {
    dispatch(setTrims([...initialFilters.trims]));
  };

  const toggleSelection = () => {
    const filteredTrims = searchText
      ? [...initialFilters.trims]
          .sort(compareValuesCaseSensitive)
          .filter(
            (item) =>
              item.toLowerCase().includes(searchText.toLowerCase()) && countByTrim(item) > 0,
          )
      : [...initialFilters.trims]
          .sort(compareValuesCaseSensitive)
          .filter((item) => countByTrim(item) > 0);
    if (!searchText) {
      filters.trims.length > 0 ? dispatch(setTrims([])) : dispatch(setTrims([...filteredTrims]));
    } else {
      filters.trims.length > 0 && filteredTrims.some((trim) => filters.trims.includes(trim))
        ? dispatch(setTrims(filters.trims.filter((trim) => !filteredTrims.includes(trim))))
        : dispatch(setTrims(Array.from(new Set([...filteredTrims, ...filters.trims]))));
    }
  };

  const countByTrim = (trim: string) => {
    const count = applyFilters(
      unfilteredData.filter((item) => item.trim === trim),
      { ...filters, trims: [...filters.trims, trim] },
    ).length;
    return count;
  };

  const label = () => {
    let result;
    if (filters.trims.length === 0 && initialFilters.trims.length > 0) {
      result = UseDropdownLabel(initialFilters.trims, initialFilters.trims, LabelType.Numbers);
    }
    result = UseDropdownLabel(
      filters.trims.filter((item) => countByTrim(item) > 0),
      initialFilters.trims,
      LabelType.Numbers,
    );

    if (result === 'All') {
      return '';
    }
    return result;
  };

  return {
    handleChange,
    pickedTrims: [...filters.trims].filter(
      (item) =>
        item.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()) && countByTrim(item) > 0,
    ),
    allTrims: [...initialFilters.trims]
      .filter(
        (item) =>
          item.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()) &&
          countByTrim(item) > 0,
      )
      .sort(compareValuesCaseSensitive),
    initiallySetAllTrimsAsPicked,
    countByTrim,
    toggleSelection,
    label: label(),
    handleSearch,
    searchText,
  };
};

type UseTrimsReturnType = {
  handleChange: (trim: string) => void;
  pickedTrims: readonly string[];
  allTrims: readonly string[];
  initiallySetAllTrimsAsPicked: () => void;
  countByTrim: (trim: string) => number;
  toggleSelection: () => void;
  label: string;
  handleSearch: (text: string) => void;
  searchText: string;
};
